import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Plus, Trash2, Save, X, Edit2 } from 'lucide-react';
import { toast } from 'sonner';

const GalleryManager = ({ onUpdate }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title_en: '',
    title_ti: '',
    description_en: '',
    description_ti: '',
    image_url: ''
  });

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const response = await api.get('/gallery');
      setPhotos(response.data);
    } catch (error) {
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/gallery/${editingId}`, formData);
        toast.success('Photo updated!');
      } else {
        await api.post('/admin/gallery', formData);
        toast.success('Photo added!');
      }
      resetForm();
      fetchPhotos();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(editingId ? 'Failed to update photo' : 'Failed to add photo');
    }
  };

  const handleEdit = (photo) => {
    setFormData({
      title_en: photo.title_en,
      title_ti: photo.title_ti,
      description_en: photo.description_en || '',
      description_ti: photo.description_ti || '',
      image_url: photo.image_url
    });
    setEditingId(photo.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success('Photo deleted');
      fetchPhotos();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const resetForm = () => {
    setFormData({
      title_en: '',
      title_ti: '',
      description_en: '',
      description_ti: '',
      image_url: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Manage Gallery</h2>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-green-600 hover:bg-green-700"
        >
          {showForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          {showForm ? 'Cancel' : 'Add Photo'}
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">{editingId ? 'Edit Photo' : 'Add New Photo'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Image URL</label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                required
                placeholder="https://example.com/image.jpg"
              />
              <p className="text-xs text-gray-500 mt-1">
                Upload your image to a service like Imgur or use a direct image URL
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title (English)</label>
                <Input
                  value={formData.title_en}
                  onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Title (Tigrinya)</label>
                <Input
                  value={formData.title_ti}
                  onChange={(e) => setFormData({...formData, title_ti: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Description (English)</label>
                <Input
                  value={formData.description_en}
                  onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description (Tigrinya)</label>
                <Input
                  value={formData.description_ti}
                  onChange={(e) => setFormData({...formData, description_ti: e.target.value})}
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" />
                {editingId ? 'Update Photo' : 'Add Photo'}
              </Button>
              <Button type="button" onClick={resetForm} variant="outline">
                Cancel
              </Button>
              {editingId && (
                <Button 
                  type="button" 
                  onClick={() => {
                    handleDelete(editingId);
                    resetForm();
                  }} 
                  className="bg-red-600 hover:bg-red-700 ml-auto"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
          </form>
        </Card>
      )}

      {/* Photos Grid */}
      <div className="grid md:grid-cols-3 gap-4">
        {loading ? (
          <p className="col-span-3 text-center py-8">Loading...</p>
        ) : photos.length > 0 ? (
          photos.map((photo) => (
            <Card key={photo.id} className="overflow-hidden">
              <img 
                src={photo.image_url} 
                alt={photo.title_en}
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-bold mb-1">{photo.title_en}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {photo.description_en}
                </p>
                <div className="flex space-x-2">
                  <Button
                    onClick={() => handleEdit(photo)}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDelete(photo.id)}
                    variant="outline"
                    size="sm"
                    className="flex-1 text-red-600"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <Card className="col-span-3 p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">No photos yet. Add your first one!</p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default GalleryManager;
